import {
    mdiAccountCashOutline,
    mdiAlertOctagonOutline,
    mdiArchiveCheckOutline,
    mdiCashClock,
    mdiClipboardTextOutline,
    mdiClockAlertOutline,
    mdiCog,
    mdiDomainOff,
    mdiFileCancelOutline,
    mdiFileDocumentOutline,
    mdiForward,
    mdiHelpCircleOutline,
    mdiHospitalBoxOutline,
    mdiKeyOutline,
    mdiPauseCircleOutline,
    mdiReplay,
    mdiSend,
    mdiShieldCheckOutline,
    mdiTimerSand,
    mdiViewSplitVertical,
} from '@mdi/js';
import { humanizeKey } from './configuration.utils';
import { WORKFLOW_STATUS_DESCRIPTIONS } from './configuration-worker-execution-order';

interface WorkflowStatusMeta {
    icon: string;
    label: string;
}

/*
 * Icons are shared by family, not unique per worker:
 * coding · waiting · documents · splitting · first send · resend · next payer · terminal outcomes.
 */
const CODING = mdiClipboardTextOutline;
const WAITING = mdiTimerSand;
const DOCUMENTS = mdiFileDocumentOutline;
const SPLIT = mdiViewSplitVertical;
const RESEND = mdiReplay;
const NEXT_PAYER = mdiForward;
const PROBLEM_OUTCOME = mdiAlertOctagonOutline;

const STATUS_META: Record<string, WorkflowStatusMeta> = {
    UNKNOWN: { label: 'Unknown', icon: mdiHelpCircleOutline },
    NEEDS_VENDOR_CODING: { label: 'Needs Vendor Coding', icon: CODING },
    EMR_WAIT: { label: 'EMR Wait', icon: WAITING },
    NEEDS_ENTER_CODING: { label: 'Needs Enter Coding', icon: CODING },
    EXECUTE_ENTER_CODING: { label: 'Execute Enter Coding', icon: CODING },
    WAIT_FOR_PROFESSIONAL_CREATED: { label: 'Wait For Professional Created', icon: WAITING },
    HALTED: { label: 'Halted', icon: mdiPauseCircleOutline },
    NEEDS_PRIMARY_VOID: { label: 'Claim Needs Primary Void', icon: mdiFileCancelOutline },
    TIMELY_FILING: { label: 'Timely Filing', icon: mdiClockAlertOutline },
    NEED_CLAIM_DOCUMENTS: { label: 'Need Claim Documents', icon: DOCUMENTS },
    NEED_MEDICAL_RECORDS: { label: 'Need Medical Records', icon: DOCUMENTS },
    NEED_PRIMARY_AUTHORIZATION_NUMBER: { label: 'Need Primary Authorization Number', icon: mdiKeyOutline },
    NEED_PRIMARY_ELIGIBILITY_CHECK: { label: 'Need Primary Eligibility Check', icon: mdiShieldCheckOutline },
    CLAIM_SPLIT: { label: 'Claim Split', icon: SPLIT },
    HIGH_VALUE_CLAIM_SPLIT: { label: 'High Value Claim Split', icon: SPLIT },
    NEED_PRIMARY_TRANSMIT: { label: 'Need Primary Transmit', icon: mdiSend },
    NEED_PRIMARY_NON_ACKNOWLEDGED_TRANSMIT: { label: 'Need Primary Non Acknowledged Transmit', icon: RESEND },
    REJECTION_UNENROLLED_PAYER: { label: 'Rejection Unenrolled Payer', icon: mdiDomainOff },
    NEED_PRIMARY_RETRANSMIT: { label: 'Need Primary Retransmit', icon: RESEND },
    PAYMENT_DENIED_FOR_HOSPICE_CARE: { label: 'Payment Denied For Hospice Care', icon: mdiHospitalBoxOutline },
    PAYMENT_DENIED_FOR_LACK_OF_DOCUMENTATION: { label: 'Payment Denied For Lack Of Documentation', icon: DOCUMENTS },
    PAYMENT_DENIED_FOR_NEED_NEW_SUBMISSION: { label: 'Payment Denied Needs New Submission', icon: RESEND },
    NEED_SECONDARY_TRANSMIT: { label: 'Need Secondary Transmit', icon: NEXT_PAYER },
    NEED_TERTIARY_TRANSMIT: { label: 'Need Tertiary Transmit', icon: NEXT_PAYER },
    NEED_GUARANTOR_COLLECTIONS: { label: 'Need Guarantor Collections', icon: mdiAccountCashOutline },
    NEEDS_CLOSED: { label: 'Claim Needs Closed', icon: mdiArchiveCheckOutline },
    DENIED_CLAIM: { label: 'Denied Claim', icon: PROBLEM_OUTCOME },
    REJECTED_CLAIM: { label: 'Rejected Claim', icon: PROBLEM_OUTCOME },
    WAITING_FOR_PAYMENT: { label: 'Waiting For Payment', icon: mdiCashClock },
};

export function getWorkflowStatusIcon(workflowStatus: string): string {
    return STATUS_META[workflowStatus]?.icon ?? mdiCog;
}

export function getWorkflowStatusLabel(workflowStatus: string): string {
    return STATUS_META[workflowStatus]?.label ?? humanizeKey(workflowStatus);
}

export function getWorkflowStatusDescription(workflowStatus: string): string {
    return WORKFLOW_STATUS_DESCRIPTIONS[workflowStatus] ?? '';
}
